import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";

/**
 * Enhanced PageHero with parallax, ornaments and animated scroll indicator.
 */
export default function PageHero({ eyebrow, title, italic, image, blurb }) {
  const bgRef = useRef(null);

  // simple parallax on scroll
  useEffect(() => {
    const el = bgRef.current;
    if (!el) return;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        if (y < window.innerHeight) {
          el.style.transform = `translate3d(0, ${y * 0.18}px, 0) scale(1.06)`;
        }
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section className="relative overflow-hidden" style={{ background: "var(--navy-deep)" }}>
      {/* Background image with parallax + ken-burns */}
      <div ref={bgRef} className="absolute inset-0 kenburns will-change-transform">
        <img src={image} alt="" className="w-full h-full object-cover opacity-45" />
      </div>

      {/* Multi-layer overlay */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(6,13,34,0.45) 0%, rgba(6,13,34,0.78) 65%, rgba(6,13,34,0.95) 100%)",
        }}
      />
      <div className="absolute inset-0 mandala-bg" style={{ opacity: 0.22 }} />

      {/* Floating gold dots */}
      <div className="absolute left-[10%] top-[20%] w-1.5 h-1.5 rounded-full bg-[var(--gold)] opacity-70 float-y" />
      <div className="absolute right-[12%] top-[35%] w-1 h-1 rounded-full bg-[var(--gold)] opacity-60 float-y" style={{ animationDelay: "1s" }} />
      <div className="absolute left-[18%] bottom-[28%] w-1 h-1 rounded-full bg-[var(--gold)] opacity-50 float-y" style={{ animationDelay: "2s" }} />
      <div className="absolute right-[20%] bottom-[20%] w-1.5 h-1.5 rounded-full bg-[var(--gold)] opacity-70 float-y" style={{ animationDelay: "1.5s" }} />

      {/* Content */}
      <div className="relative max-w-[1200px] mx-auto px-5 md:px-10 py-24 md:py-36 text-center">
        <div className="eyebrow mb-5 fade-up">{eyebrow}</div>
        <h1 className="h-display text-white fade-up" style={{ animationDelay: "100ms" }}>
          {title}
          <span className="block serif-italic gold-text">{italic}</span>
        </h1>
        <span className="gold-divider mx-auto fade-up" style={{ animationDelay: "200ms" }} />
        {blurb && (
          <p className="serif text-white/85 text-[16px] md:text-[20px] max-w-[760px] mx-auto leading-[1.75] fade-up" style={{ animationDelay: "300ms" }}>
            {blurb}
          </p>
        )}
      </div>

      {/* breadcrumb + scroll cue */}
      <div className="relative max-w-[1200px] mx-auto px-5 md:px-10 pb-8 -mt-2 md:-mt-4 flex flex-col items-center gap-4">
        <div className="flex items-center gap-2 text-[10.5px] md:text-[11px] tracking-[0.22em] uppercase text-[var(--gold-light)]">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <ArrowRight size={11} />
          <span className="text-white/85">{title} {italic}</span>
        </div>
        <ChevronDown size={18} className="text-[var(--gold-light)] float-y" />
      </div>
    </section>
  );
}
